import { useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from '@hookform/error-message';
import { Col, Form, InputNumber, Row, Switch } from 'antd';
import { InputValidationError } from 'pos-client/components/generic/input-validation-error/InputValidationError.tsx';
import { createFormFields } from 'pos-client/components/shared/Form/FormUtils.ts';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import { sortStrings } from 'pos-client/utils/dataTableHelpers.tsx';
import type { RouteModel } from 'pos-shared/models/resourceManagementSchema.ts';
import type { RouteRechargeSettingsModel } from 'pos-shared/models/routeSchema.ts';

const rechargePolicyOptions = [
  {
    value: 'AnyDepot',
    key: 'AnyDepot',
  },
  {
    value: 'InitialDepot',
    key: 'InitialDepot',
  },
  {
    value: 'FinalDepot',
    key: 'FinalDepot',
  },
  {
    value: 'InitialOrFinal',
    key: 'InitialOrFinal',
  },
];

interface RouteRechargeSettingsProps {
  form: UseFormReturn<RouteRechargeSettingsModel>;
  route?: RouteModel;
}

const RechargeSettingsTabContent = ({
  form,
  route,
}: RouteRechargeSettingsProps) => {
  const [allowRechargesValue, setAllowRechargesValue] = useState(
    route?.rechargeSettings?.allowRecharges
  );

  const { t } = useTranslation();
  return (
    <Form
      fields={createFormFields(form)}
      layout="vertical"
      requiredMark={false}
    >
      <Row>
        <Col span={24}>
          <Controller
            control={form.control}
            name="rechargeSettings.allowRecharges"
            render={({ field: { name, value, onChange } }) => (
              <Form.Item
                label={`${t('routes.rechargeSettings.allowRecharges')}?`}
              >
                <Switch
                  onChange={(value) => {
                    setAllowRechargesValue(value);
                    onChange(value);
                  }}
                  checked={!!value}
                />
                <ErrorMessage
                  errors={form.formState.errors}
                  name={name}
                  render={InputValidationError}
                />
              </Form.Item>
            )}
          />
        </Col>
      </Row>
      {allowRechargesValue && (
        <>
          <Row>
            <Col span={24}>
              <PosSelect
                control={form.control}
                name="rechargeSettings.rechargePolicy"
                label={t('routes.rechargeSettings.rechargePolicy')}
                options={rechargePolicyOptions}
                filterSort={(optionA, optionB) =>
                  sortStrings(optionA, optionB, 'value')
                }
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Controller
                control={form.control}
                name="rechargeSettings.rechargeToCapacity"
                render={({ field: { name, value, onChange } }) => (
                  <Form.Item
                    label={t('routes.rechargeSettings.rechargeToCapacity')}
                  >
                    <Switch
                      onChange={(value) => {
                        onChange(value);
                      }}
                      checked={!!value}
                    />
                    <ErrorMessage
                      errors={form.formState.errors}
                      name={name}
                      render={InputValidationError}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Controller
                control={form.control}
                name="settings.limits.maxDistance"
                // Zod validation for all routes forms coming in next PR
                rules={{
                  required: {
                    value: true,
                    message: t('routes.rechargeSettings.maxDistance'),
                  },
                  min: {
                    value: 100000,
                    message: t('routes.rechargeSettings.maxDistanceError'),
                  },
                }}
                render={({ field: { onChange, value, name } }) => (
                  <Form.Item
                    label={`${t(
                      'routes.rechargeSettings.maxDistance'
                    )} (${t('routes.rechargeSettings.metersUnit')})`}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      onChange={onChange}
                      value={value}
                    />
                    <ErrorMessage
                      errors={form.formState.errors}
                      name={name}
                      render={InputValidationError}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Form>
  );
};

export default RechargeSettingsTabContent;
